import React from 'react'

interface ResearchDepthSelectorProps {
  onStartResearch: () => void
  isLoading?: boolean
  disabled?: boolean
  canStart?: boolean
  compact?: boolean
}

// Diamond icon for Expert Report
const DiamondIcon = ({ className = 'w-8 h-8' }: { className?: string }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.5"
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M6 3h12l4 6-10 13L2 9z" />
    <path d="M2 9h20" />
    <path d="M12 22L6 9" />
    <path d="M12 22l6-13" />
    <path d="M6 3l6 6 6-6" />
  </svg>
)

export function ResearchDepthSelector({
  onStartResearch,
  isLoading = false,
  disabled = false,
  canStart = true,
  compact = false,
}: ResearchDepthSelectorProps) {
  return (
    <div
      className="w-full"
      data-testid="research-depth-selector"
      data-compact={compact ? 'true' : 'false'}
    >
      {/* Single Expert Report Card - No selection needed */}
      <div
        className={`
          relative rounded-2xl transition-all duration-300 ease-out
          border-2 border-emerald-400/60 bg-emerald-400/10 dark:bg-emerald-500/10 
          shadow-lg shadow-emerald-500/10
          ${compact ? 'p-3.5' : 'p-5'}
          ${disabled || isLoading ? 'opacity-50' : ''}
        `}
      >
        {compact ? (
          <>
            <div className="mb-2 flex items-center gap-2">
              <div className="text-emerald-600 dark:text-emerald-400">
                <DiamondIcon className="h-5 w-5" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Expert Report
              </h3>
              <span className="ml-auto text-[10px] font-mono text-emerald-600 dark:text-emerald-400">
                45-75 minutes
              </span>
            </div>
            <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-200">
              Analyst-grade intelligence for critical decisions.
            </p>
          </>
        ) : (
          <>
            <div className="mb-3 text-emerald-600 dark:text-emerald-400">
              <DiamondIcon />
            </div>

            <h3 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
              Expert Report
            </h3>

            <div className="mb-2">
              <span className="text-sm font-mono text-emerald-600 dark:text-emerald-400">
                45-75 minutes
              </span>
            </div>

            <div className="mb-3 h-px w-full bg-emerald-200 dark:bg-emerald-800" />

            <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">
              Comprehensive analyst-grade intelligence for critical decisions
            </p>
          </>
        )}

        {/* Start Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            if (!canStart || isLoading || disabled) return
            onStartResearch()
          }}
          disabled={isLoading || !canStart || disabled}
          className={`
            w-full rounded-xl px-4 font-medium text-white transition-all duration-200
            ${compact ? 'mt-3 py-2 text-[13px]' : 'mt-5 py-2.5 text-sm'}
            flex items-center justify-center gap-2
            bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800
            ${isLoading ? 'opacity-75 cursor-wait' : ''}
            ${!canStart || disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Starting Research...</span>
            </>
          ) : (
            <>
              <span>Start Research</span>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default ResearchDepthSelector
